import { graphql } from '@eluve/graphql.tada';

import type { TenantBundleBlocker } from './BundleBlockerTooltip';

/**
 * Fetched once per tenant admin page so the per-row toggle in the
 * prompt-templates / output-templates tables can derive in memory whether
 * the row is blocked by an active tenant bundle (no N+1 queries).
 */
export const getTenantEnabledBundlesForDepsQuery = graphql(`
  query getTenantEnabledBundlesForDeps($tenantId: uuid!) {
    tenantTemplateBundles(
      where: {
        tenantId: { _eq: $tenantId }
        isEnabled: { _eq: true }
        llm_model_prompt_template: {
          isBundle: { _eq: true }
          isActive: { _eq: true }
        }
      }
    ) {
      __typename
      tenantId
      llmModelPromptTemplateId
      llm_model_prompt_template {
        __typename
        id
        displayName
        promptTemplateId
        outputTemplateId
      }
    }
  }
`);

interface EnabledBundleRow {
  llm_model_prompt_template: {
    id: string;
    displayName: string | null;
    promptTemplateId: string;
    outputTemplateId: string | null;
  };
}

/**
 * Index the enabled-bundle rows by the FK we're about to look up (the
 * prompt or output template id) so each row in the admin table can find
 * its blockers in O(1). Returns an empty map when `bundles` is null/empty.
 */
export const groupBlockersById = (
  bundles: readonly EnabledBundleRow[] | null | undefined,
  getKey: (row: EnabledBundleRow) => string | null,
): Map<string, TenantBundleBlocker[]> => {
  const map = new Map<string, TenantBundleBlocker[]>();
  for (const ttb of bundles ?? []) {
    const key = getKey(ttb);
    if (!key) continue;
    const existing = map.get(key);
    const entry: TenantBundleBlocker = {
      bundleId: ttb.llm_model_prompt_template.id,
      bundleDisplayName: ttb.llm_model_prompt_template.displayName,
    };
    if (existing) {
      existing.push(entry);
    } else {
      map.set(key, [entry]);
    }
  }
  return map;
};
